import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchAttachmentPopupProps } from '@v2/components/imageHandling/components/Header/components/AttachModal/SearchAttachmentPopup';
import { Alert, getValueByPath, isPhone, isWeb } from '@helpers';
import { useSelector } from 'react-redux';
import {
  selectUpdatingImageMetadataInProgress,
  updateImageDocumentInDict,
} from '../../../../../../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { setUpdatingImageMetadataInProgress } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { updateImageMetadata } from '@reducers/v2/imageHandlingV2/thunks';
import { SearchItemType } from '@interfaces/base';
import { KEYS_TAGS } from '@components/imageHandling/common';

interface UseManageSearchAttachmentPopupParams
  extends Pick<SearchAttachmentPopupProps, 'attachImageId' | 'onAttachSuccess' | 'hide' | 'imageDocument'> {}

export const useManageSearchAttachmentPopup = ({ attachImageId, onAttachSuccess, hide, imageDocument }: UseManageSearchAttachmentPopupParams) => {
  console.log('SearchAttachmentPopup received attachImageId:', attachImageId);

  const [selectedItem, setSelectedItem] = useState<{ item: Record<string, unknown>; itemType: SearchItemType; itemName: string } | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const dispatch = useAppDispatch();

  const updatingImageMetadataInProgress = useSelector(selectUpdatingImageMetadataInProgress);

  // Use fixed width for tablets to prevent expansion
  const fixedPopupWidth = useMemo(() => {
    if (!isWeb && !isPhone) {
      return 700; // Fixed width for tablets
    }
    return null; // Use dynamic width for phones
  }, []);

  const [popupWidth, setPopupWidth] = useState(fixedPopupWidth);

  const handleItemSelect = useCallback((item: Record<string, unknown>, itemType: SearchItemType) => {
    // Extract the correct name field based on item type
    let itemName = 'Unknown';
    switch (itemType) {
      case 'Part':
        itemName = item.partName as string;
        break;
      case 'Operation':
        itemName = item.productionOperationNumber as string;
        break;
      case 'NcFile':
        itemName = item.ncFileName as string;
        break;
      case 'ProductionOrder':
        itemName = item.productionOrderNumber as string;
        break;
      case 'Tool':
        itemName = item.toolName as string;
        break;
      case 'Machine':
        itemName = item.fullName as string;
        break;
      case 'UserAccount':
        itemName = `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}` as string;
        break;
      default:
        itemName = (item.name || item.title || item.description || 'Unknown') as string;
    }

    setSelectedItem({ item, itemType, itemName });
    setShowConfirmationModal(true);
  }, []);

  useEffect(() => {
    if (showConfirmationModal && !updatingImageMetadataInProgress) {
      setShowConfirmationModal(false);
    }
  }, [updatingImageMetadataInProgress]);

  const handleConfirmAttachment = useCallback(async () => {
    console.log('handleConfirmAttachment called:', { selectedItem, attachImageId, isAttaching });

    dispatch(setUpdatingImageMetadataInProgress(true));

    if (!selectedItem || !attachImageId || isAttaching) {
      console.log('Early return:', { selectedItem: !!selectedItem, attachImageId: !!attachImageId, isAttaching });
      dispatch(setUpdatingImageMetadataInProgress(false));
      return;
    }

    setIsAttaching(true);

    try {
      const { item, itemType, itemName } = selectedItem;
      console.log('Attaching item:', { item, itemType, itemName });

      const itemTypeIsUser = itemType === 'UserAccount';
      const scopeReferenceId = getItemId(item, itemType);

      const newLink = {
        scopeId: KEYS_TAGS[itemType],
        ...(!itemTypeIsUser ? { scopeReferenceId } : {}),
        scopeReferenceName: itemTypeIsUser ? scopeReferenceId : itemName, // userId exposed to the app is a Guid which must be provided in the scopeReferenceName field (instead of scopeReferenceId, scopeReferenceId should be omitted)
        isFavorite: false,
        isActivated: true,
        isFavoriteChanged: false,
      };

      // Local update for immediate feedback for user
      dispatch(
        updateImageDocumentInDict({
          documentId: attachImageId,
          updatedImageDocument: { ...imageDocument, links: [...imageDocument.links, newLink] },
        })
      );

      dispatch(setUpdatingImageMetadataInProgress(false));

      // Close popup
      hide();

      // Create attachment data based on the selected item and itemType
      const attachmentData = {
        documentId: attachImageId,
        documentLinks: [newLink],
      };

      console.log('Attachment data:', attachmentData);

      // Update image metadata with new attachment
      const result = await dispatch(
        updateImageMetadata({ data: attachmentData, ...(onAttachSuccess ? { onAttachSuccess: () => onAttachSuccess(attachmentData) } : {}) })
      );
      console.log('updateImageMetadata result:', result);
    } catch (e) {
      const error = e as Error;
      console.error('Error attaching item:', error);
      Alert({ text: 'Error attaching item: ' + error.message });
    } finally {
      setIsAttaching(false);
      setSelectedItem(null);
    }
  }, [selectedItem, attachImageId, isAttaching, updateImageMetadata, onAttachSuccess, hide]);

  const handleCancelAttachment = useCallback(() => {
    setShowConfirmationModal(false);
    setSelectedItem(null);
  }, []);

  const getItemId = (item: Record<string, unknown>, itemType: SearchItemType): string | number => {
    const idMapping: Record<SearchItemType, string> = {
      Part: 'partId',
      Operation: 'productionOperationId',
      ProductionOrder: 'productionOrderId',
      NcFile: 'ncFileId',
      Tool: 'toolId',
      Machine: 'machineId',
      UserAccount: 'userProfile/userId',
    };

    const value = getValueByPath(item, idMapping[itemType]);

    if (typeof value !== 'number' && typeof value !== 'string') {
      throw new Error(`Invalid id for itemType "${itemType}"`);
    }

    return value;
  };

  return {
    fixedPopupWidth,
    setPopupWidth,
    handleItemSelect,
    popupWidth,
    selectedItem,
    showConfirmationModal,
    handleCancelAttachment,
    handleConfirmAttachment,
  };
};
